/**
 * In-session greeting rotation. For each (mode, tone) key it holds a shuffled
 * order and a cursor; each request returns the next index and advances, and
 * reshuffles (avoiding an immediate repeat) when the set is exhausted.
 */

type Rng = () => number;

type RotationEntry = {
	order: number[];
	cursor: number;
	/** Index most recently returned, used to avoid an immediate repeat on reshuffle. */
	last: number;
};

const rotationState = new Map<string, RotationEntry>();

/** Fisher-Yates shuffle over `[0, length)`, using the supplied RNG. */
const shuffledRange = (length: number, rng: Rng): number[] => {
	const order = Array.from({ length }, (_, i) => i);
	for (let i = order.length - 1; i > 0; i--) {
		const j = Math.floor(rng() * (i + 1));
		[order[i], order[j]] = [order[j], order[i]];
	}
	return order;
};

/**
 * Returns the next index to show for `key` within a set of `length` items, and
 * advances the rotation. Returns -1 for an empty set.
 */
const next = (key: string, length: number, rng: Rng = Math.random): number => {
	if (length <= 0) {
		return -1;
	}
	if (length === 1) {
		return 0;
	}

	let entry = rotationState.get(key);

	if (!entry || entry.cursor >= entry.order.length) {
		const order = shuffledRange(length, rng);
		// Avoid showing the same greeting twice in a row across a reshuffle.
		if (entry && order[0] === entry.last) {
			const swapWith = 1 + Math.floor(rng() * (order.length - 1));
			[order[0], order[swapWith]] = [order[swapWith], order[0]];
		}
		entry = { order, cursor: 0, last: entry?.last ?? -1 };
	}

	const index = entry.order[entry.cursor];
	rotationState.set(key, { order: entry.order, cursor: entry.cursor + 1, last: index });
	return index;
};

/** Test-only: clear all rotation state so cases start from a known point. */
const reset = (): void => {
	rotationState.clear();
};

type GreetingRotation = {
	next: (key: string, length: number, rng?: Rng) => number;
	reset: () => void;
};

export const greetingRotation: GreetingRotation = { next, reset };
